import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { ApprovalOutcomeChip } from "./ApprovalChips";
import { EMatrixError, conditionLabels, } from "./approvalMatrix";
import { AccentTag, Muted, Notice, NumberBadge, Tag, TagRow } from "./approvalStyles";
var RuleCard = styled("li")(function (_a) {
    var theme = _a.theme;
    return ({
        "display": "flex",
        "gap": "16px",
        "padding": "16px",
        "border": "1px solid #E3E7EF",
        "borderRadius": "10px",
        "background": theme.palette.white,
        "listStyle": "none",
        "&[data-highlighted='true']": {
            borderColor: theme.palette.brandSuccess,
            boxShadow: "inset 0 0 0 1px ".concat(theme.palette.brandSuccess),
            background: "#F0FBF6",
        },
    });
});
var RuleList = styled("ol")({
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    margin: 0,
    padding: 0,
});
var Line = styled("div")({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px",
    minHeight: "32px",
});
var Word = styled("span")({
    minWidth: "40px",
    color: "#3D4353",
    fontSize: "14px",
    fontWeight: 600,
});
var Actions = styled("div")({
    display: "flex",
    alignItems: "flex-start",
    marginLeft: "auto",
    flexShrink: 0,
});
export var ApprovalMatrixRules = function (_a) {
    var matrix = _a.matrix, canEdit = _a.canEdit, fieldLabel = _a.fieldLabel, appliesTo = _a.appliesTo, cameFrom = _a.cameFrom, _b = _a.problems, problems = _b === void 0 ? [] : _b, onEdit = _a.onEdit, onMove = _a.onMove, onDelete = _a.onDelete, onAdd = _a.onAdd;
    var t = useTranslation().t;
    var conditions = function (when) {
        var labels = conditionLabels(when, t, fieldLabel);
        return labels.map(function (label, index) { return (_jsxs(React.Fragment, { children: [_jsx(Tag, { children: label }), index < labels.length - 1 && _jsx(Muted, { children: t("approvalsScreen.matrix.andWord") })] }, label)); });
    };
    var outcome = function (then) { return (_jsxs(Line, { children: [_jsx(Word, { children: t("approvalsScreen.matrix.then") }), _jsx(ApprovalOutcomeChip, { decision: then.decision }), then.reason && (_jsx(Muted, { children: t("approvalsScreen.matrix.voterIsTold", {
                    reason: t("approvalsScreen.matrix.reasons.".concat(then.reason)),
                }) }))] })); };
    var pointers = function (position) {
        return (appliesTo === position || cameFrom === position) && (_jsxs(TagRow, { sx: { marginTop: "8px" }, children: [appliesTo === position && (_jsx(AccentTag, { children: t("approvalsScreen.matrix.appliesToExample") })), cameFrom === position && (_jsx(AccentTag, { children: t("approvalsScreen.matrix.cameFrom") }))] }));
    };
    var errors = function (position) {
        var found = problems.filter(function (_a) {
            var code = _a.code, rule = _a.rule;
            return rule === position && code !== EMatrixError.NO_COMPARED_FIELDS;
        });
        return (found.length > 0 && (_jsx(Notice, { "data-tone": "error", role: "alert", sx: { marginTop: "8px", padding: "8px 12px" }, children: _jsx("div", { children: found.map(function (_a) {
                    var code = _a.code;
                    return (_jsx("div", { children: t("approvalsScreen.matrix.errors.".concat(code)) }, code));
                }) }) })));
    };
    return (_jsxs(_Fragment, { children: [_jsxs(RuleList, { "aria-label": String(t("approvalsScreen.matrix.rules")), children: [matrix.rules.map(function (rule, index) {
                        var number = index + 1;
                        return (_jsxs(RuleCard, { "data-highlighted": appliesTo === number || cameFrom === number, children: [_jsx(NumberBadge, { children: number }), _jsxs(Box, { sx: { flexGrow: 1, minWidth: 0 }, children: [_jsxs(Line, { children: [_jsx(Word, { children: t("approvalsScreen.matrix.when") }), conditions(rule.when)] }), outcome(rule.then), pointers(number), errors(number)] }), canEdit && (_jsxs(Actions, { children: [_jsx(IconButton, { size: "small", "aria-label": String(t("approvalsScreen.matrix.actions.moveUp", { number: number })), disabled: index === 0, onClick: function () { return onMove(index, -1); }, children: _jsx(ArrowUpwardIcon, { fontSize: "small" }) }), _jsx(IconButton, { size: "small", "aria-label": String(t("approvalsScreen.matrix.actions.moveDown", { number: number })), disabled: index === matrix.rules.length - 1, onClick: function () { return onMove(index, 1); }, children: _jsx(ArrowDownwardIcon, { fontSize: "small" }) }), _jsx(IconButton, { size: "small", "aria-label": String(t("approvalsScreen.matrix.actions.edit", { number: number })), onClick: function () { return onEdit(index); }, children: _jsx(EditIcon, { fontSize: "small" }) }), _jsx(IconButton, { size: "small", "aria-label": String(t("approvalsScreen.matrix.actions.delete", { number: number })), onClick: function () { return onDelete(index); }, children: _jsx(DeleteOutlineIcon, { fontSize: "small" }) })] }))] }, index));
                    }), _jsxs(RuleCard, { "data-highlighted": appliesTo === null || cameFrom === null, children: [_jsx(NumberBadge, { "data-muted": "true", "aria-hidden": true, children: "\u2022" }), _jsxs(Box, { sx: { flexGrow: 1, minWidth: 0 }, children: [_jsxs(Line, { children: [_jsx(Word, { children: t("approvalsScreen.matrix.otherwise") }), _jsx("span", { children: t("approvalsScreen.matrix.noneApply") })] }), outcome(matrix.otherwise), pointers(null), errors(null)] }), canEdit && (_jsx(Actions, { children: _jsx(IconButton, { size: "small", "aria-label": String(t("approvalsScreen.matrix.actions.editOtherwise")), onClick: function () { return onEdit(null); }, children: _jsx(EditIcon, { fontSize: "small" }) }) }))] })] }), canEdit && (_jsx(Box, { sx: { marginTop: "16px" }, children: _jsx(Button, { variant: "secondary", onClick: onAdd, startIcon: _jsx(AddIcon, {}), children: t("approvalsScreen.matrix.addRule") }) }))] }));
};
